// D-59: shouldSuppressLangTabs and cardHasPerLanguageText helpers
// VoiceSpectrum.Web.T2 · v10.0.8.5 · 2026-05-17 · VoiceSpectrum AI

import type { WorkflowNode, PreRoutingNode } from '../data';

type CardLike = WorkflowNode | PreRoutingNode | { id?: string; type?: string; silent?: boolean; sections?: { fields?: { perLanguage: boolean }[] }[] };

/**
 * D-59: Returns true if this card emits zero spoken/written caller-facing text.
 * When true, the EN/ES language tab strip is suppressed.
 *
 * Cards that suppress: Inbound (always silent), Hours Check (timezone/schedule are universal),
 * silent Connect cards (webhook-only, silent === true).
 * All other cards show tabs if they have at least one perLanguage field.
 */
export function shouldSuppressLangTabs(card: CardLike): boolean {
  // Inbound is always silent — D-53, D-53b
  const SILENT_CARDS = new Set(['inbound']);
  if ('id' in card && card.id && SILENT_CARDS.has(String(card.id))) return true;

  // Connect nodes: silent webhook-only variant suppresses tabs
  if ('type' in card && card.type === 'Connect' && (card as { silent?: boolean }).silent === true) return true;

  // Hours Check has no per-language fields (timezone, schedule, holidays are universal)
  if ('type' in card && (card.type === 'hours_check' || (card as { id?: string }).id === 'hours-check')) return true;
  if ('role' in card && (card as PreRoutingNode).role === 'hours') return true;

  // Default: suppress only if card has NO per-language text fields
  return !cardHasPerLanguageText(card);
}

/**
 * Returns true if card has at least one field marked perLanguage === true.
 */
export function cardHasPerLanguageText(card: CardLike): boolean {
  if ('sections' in card && card.sections) {
    return card.sections.some(
      sec => sec.fields && sec.fields.some((f) => f.perLanguage === true),
    );
  }

  // For known node types, infer from type
  if ('type' in card) {
    const t = (card as WorkflowNode).type;
    // These types always have per-language content
    if (['Identity', 'Request', 'Steps', 'Confirm'].includes(t ?? '')) return true;   // v12.7.3 (#648): Request too
    // Connect: only if not silent
    if (t === 'Connect') return !(card as WorkflowNode).silent;
  }

  // PreRoutingNodes: roles that have per-language content
  if ('role' in card) {
    const role = (card as PreRoutingNode).role;
    if (['disclosure', 'greeting', 'lang', 'classify'].includes(role)) return true;
    if (role === 'hours') return false;  // Hours Check: universal only
    if (role === 'start') return false;  // Inbound: no per-language
  }

  return false;
}
